import type { DocNode } from '@atlaskit/adf-schema/doc';

import { createAdfDerivationMemo } from './createAdfDerivationMemo.ts';

export type ParseAdfResult = { ok: true; doc: DocNode } | { ok: false; error: Error };

const parsedAdfByContent = createAdfDerivationMemo<ParseAdfResult>(2);

const parse = (adfString: string): ParseAdfResult => {
	try {
		return { ok: true, doc: JSON.parse(adfString) as DocNode };
	} catch (error) {
		return {
			ok: false,
			error: error instanceof Error ? error : new Error('Failed to parse ADF'),
		};
	}
};

/**
 * Parses a content item's complete body ADF, reusing the shared client cache when available.
 * Use for full content bodies, not comments, messages, macro bodies, or other ADF fragments.
 * Treat the returned document as read-only. During SSR, parsing bypasses the shared cache.
 */
export const getOrParseContentBodyAdf = (contentId: string, adfString: string): ParseAdfResult => {
	// Unlike the reference-keyed WeakMap caches, this string-keyed LRU strongly retains
	// parsed documents. Bypass it during SSR so document objects are not retained or
	// reused across requests by the long-lived Node process.

	// This controls process-global cache retention, not rendered output.
	// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
	if (process.env.REACT_SSR) {
		return parse(adfString);
	}

	return parsedAdfByContent.derive(contentId, adfString, () => parse(adfString));
};

export const seedContentBodyAdfCache = (
	contentId: string,
	adfString: string,
	doc: DocNode,
): void => {
	// Keep the process-global memo client-only so SSR requests cannot retain or
	// reuse documents parsed while preparing another request.
	// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
	if (process.env.REACT_SSR) {
		return;
	}

	parsedAdfByContent.set(contentId, adfString, { ok: true, doc });
};
